import { HttpError } from "react-admin";

import { createApiClient } from "@/api";
import { HTTP_STATUS } from "@/constants/http";
import { type DataProviderSchema } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/cloudflare/landing_domains";

type LandingDomainFormFields = {
  url: string;
  region: string;
};

const fetchListData = async () => {
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: false,
      },
    },
  });

  const data = response.data?.["hydra:member"] ?? [];
  const total = response.data?.["hydra:totalItems"] ?? 0;

  return {
    data,
    total,
  };
};

export const landingDomainsSchema: DataProviderSchema<
  "LandingDomain",
  "jsonld"
> = {
  list: getLocalFilteredSortedList(fetchListData),
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });

    if (!data) throw new Error("LandingDomain not found");

    return { data };
  },
  create: async ({ data: form }) => {
    const { url, region } = form as LandingDomainFormFields;
    const { data } = await apiClient.POST(BASE_URL, {
      body: {
        url,
        region,
      },
    });

    if (!data) throw new Error("Something went wrong!");

    return {
      data,
    };
  },
  delete: (async (entity: { id: string }) =>
    await apiClient.DELETE(`${BASE_URL}/{id}`, {
      params: {
        path: { id: entity.id },
      },
    })) as unknown as NonNullable<
    DataProviderSchema<"LandingDomain", "jsonld">["delete"]
  >,
  edit: async ({ id, data: { expiresAt } }) => {
    const response = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        expiresAt,
      },
    });

    if (!response.data)
      throw new HttpError(
        "Something went wrong!",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );

    return {
      data: response.data,
    };
  },
};
